/**
 * Ads gate — the ONE utility every gated action calls.
 *
 * Usage:
 *   import { runGated } from "@/lib/adsGate";
 *   const ok = await runGated("tryons", () => api.post("/tryon", form));
 *
 * runGated:
 *   1. Fires the action optimistically.
 *   2. If backend returns 402 needs_ad → opens the AdModal, watches, calls
 *      /ads/prepare + /ads/reward, then retries the action once.
 *   3. If backend returns 402 needs_upgrade → opens the UpgradeModal.
 *   4. Any other error is thrown to the caller.
 */
import { api } from "@/lib/api";
import { openAdModal } from "@/components/AdModal";
import { openUpgradeModal } from "@/components/UpgradeModal";
import { toast } from "sonner";

async function watchAdAndReward(feature) {
  // Prepare (sign the server nonce).
  const { data: prep } = await api.post("/ads/prepare", { feature });
  if (prep.skip) return true; // paid or unlimited
  if (!prep.token) {
    openUpgradeModal(feature);
    return false;
  }
  // `ssv_data` binds the reward to this user+feature on Google's SSV
  // callback — see /webhooks/admob/reward. Passing through to AdModal so
  // it can attach as customData on the native rewarded ad request.
  const finished = await openAdModal(feature, { ssvData: prep.ssv_data });
  if (!finished) return false; // user closed
  try {
    await api.post("/ads/reward", { feature, token: prep.token });
    return true;
  } catch (e) {
    if (e?.response?.status === 402) {
      openUpgradeModal(feature);
      return false;
    }
    toast.error("Couldn't grant the reward. Please retry.");
    return false;
  }
}

export async function runGated(feature, action, opts = {}) {
  try {
    return await action();
  } catch (e) {
    if (e?.response?.status !== 402) throw e;
    const detail = e.response.data?.detail || {};
    if (detail.error === "needs_upgrade") {
      openUpgradeModal(feature);
      throw e;
    }
    if (detail.error === "needs_ad") {
      const unlocked = await watchAdAndReward(feature);
      if (!unlocked) throw e;
      // Retry the action once.
      return await action();
    }
    throw e;
  }
}

/** Pre-check quota — useful before showing "you have N left" UI. */
export async function getQuota(feature) {
  const { data } = await api.get(`/ads/quota?feature=${encodeURIComponent(feature)}`);
  return data;
}

/** Bulk pre-check — for a "usage today" dashboard row. */
export async function getAllQuotas() {
  const { data } = await api.get("/ads/summary");
  return data;
}
